import { useState } from 'react';

import { mapLeaveType, LEAVE_TYPES } from 'utils/common';
import dayjs from 'lib/dayjs';
import Icon from '../../../components/AppIcon';

const EventListModal = ({
  isOpen,
  onClose,
  selectedDate,
  employeeEvents = [],
  events,
  leaves = [],
  onEdit,
  onDelete,
  onAdd,
  isAdmin = true,
}) => {
  const [editingEvent, setEditingEvent] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editType, setEditType] = useState('');

  const eventTypes = [
    {
      value: 'EVENT_CELEBRATION',
      label: 'Event Celebration',
      icon: 'Star',
      color: 'text-blue-600',
    },
    { value: 'FULL_HOLIDAY', label: 'Full Holiday', icon: 'Calendar', color: 'text-red-600' },
    { value: 'HALF_HOLIDAY', label: 'Half Holiday', icon: 'Clock', color: 'text-orange-600' },
  ];

  const handleEditStart = (event, index) => {
    setEditingEvent(index);
    setEditTitle(event?.title);
    setEditType(event?.type);
  };

  const handleEditSave = (index) => {
    if (!editTitle?.trim()) {
      alert('Please enter an event title.');
      return;
    }

    onEdit?.(index, {
      title: editTitle?.trim(),
      type: editType,
    });

    setEditingEvent(null);
    setEditTitle('');
    setEditType('');
  };

  const handleEditCancel = () => {
    setEditingEvent(null);
    setEditTitle('');
    setEditType('');
  };

  const handleClose = () => {
    handleEditCancel();
    onClose?.();
  };

  const getEventTypeData = (type) => {
    return eventTypes?.find((et) => et?.value === type) || eventTypes?.[0];
  };

  const getLeaveTypeIcon = (type) => {
    switch (type) {
      case LEAVE_TYPES.FULL_DAY:
        return 'Calendar';
      case LEAVE_TYPES.HALF_DAY:
        return 'Clock';
      case LEAVE_TYPES.SHORT:
        return 'Timer';
      case LEAVE_TYPES.LONG_TIME:
        return 'CalendarRange';
      case LEAVE_TYPES.MULTIPLE_DAY:
        return 'CalendarDays';
      default:
        return 'FileText';
    }
  };

  const getLeaveTypeColor = () => {
    return 'text-purple-600';
  };
  const getEmployeeDisplayName = (leave) => {
    const { first_name, last_name, employee_id } = leave?.user || {};
    const employeeName = first_name + ' ' + last_name || 'Unknown Employee';
    return `${employeeName} - ${employee_id}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatLeaveDate = (leave) => {
    if (leave?.type == LEAVE_TYPES.FULL_DAY) {
      return dayjs(leave?.start_date_time).format('DD-MM-YYYY');
    }
    if (leave?.type == LEAVE_TYPES.HALF_DAY) {
      const startTime = dayjs(leave?.start_date_time).format('hh:mm A');
      const endTime = dayjs(leave?.end_date_time).format('hh:mm A');
      return `${dayjs(leave?.start_date_time).format('DD-MM-YYYY')} ${startTime} - ${endTime}`;
    }
    if (leave?.type === LEAVE_TYPES.MULTIPLE_DAY && leave?.dates?.length > 0) {
      return `${leave?.dates?.length} days selected`;
    }
    if (leave?.start_date_time && leave?.end_date_time) {
      const startDate = leave?.start_date_time?.split('T')[0];
      const endDate = leave?.end_date_time?.split('T')[0];
      if (startDate === endDate) {
        return new Date(startDate).toLocaleDateString();
      }
      return `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`;
    }
    if (leave?.startDate && leave?.endDate) {
      return `${new Date(leave?.startDate).toLocaleDateString()} - ${new Date(leave?.endDate).toLocaleDateString()}`;
    }
    if (leave?.date) {
      return new Date(leave?.date).toLocaleDateString();
    }
    return 'Date not specified';
  };

  const formatLeaveDuration = (leave) => {
    if (leave?.duration) {
      return leave?.duration;
    }

    if (leave?.type === LEAVE_TYPES.MULTIPLE_DAY && leave?.dates?.length > 0) {
      return `${leave?.dates?.length} day${leave?.dates?.length > 1 ? 's' : ''}`;
    }

    if (leave?.start_date_time && leave?.end_date_time) {
      const start = new Date(leave?.start_date_time);
      const end = new Date(leave?.end_date_time);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        const startTime = leave?.start_date_time?.split('T')[1]?.substring(0, 5);
        const endTime = leave?.end_date_time?.split('T')[1]?.substring(0, 5);
        return `${startTime} - ${endTime}`;
      }
      return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    }

    return 'Duration not specified';
  };

  const formatMultipleDayDates = (leave) => {
    if (leave?.type === LEAVE_TYPES.MULTIPLE_DAY && leave?.dates?.length > 0) {
      const sortedDates = leave?.dates;
      const firstDate = new Date(sortedDates[0]).toLocaleDateString();
      const lastDate = new Date(sortedDates[sortedDates.length - 1]).toLocaleDateString();

      if (sortedDates.length === 1) {
        return firstDate;
      } else if (sortedDates.length <= 3) {
        return sortedDates.map((date) => new Date(date).toLocaleDateString()).join(', ');
      } else {
        return `${firstDate} - ${lastDate} (${sortedDates.length} days)`;
      }
    }
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-black bg-opacity-50"
          onClick={handleClose}
        ></div>

        {/* Modal panel */}
        <div className="relative inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-surface shadow-xl rounded-2xl border border-border">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-text-primary">
                {events?.length > 0 && leaves?.length > 0
                  ? 'Events & Leaves'
                  : events?.length > 0
                    ? 'Events'
                    : leaves?.length > 0
                      ? 'Leaves'
                      : 'Events'}
              </h3>
              <p className="text-sm text-text-secondary mt-1">
                {selectedDate?.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onAdd?.(selectedDate)}
                className="p-2 text-primary hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors duration-200"
                title={isAdmin ? 'Add new event' : 'Add new leave'}
              >
                <Icon name="Plus" size={20} />
              </button>
              <button
                onClick={handleClose}
                className="p-2 text-text-secondary hover:text-text-primary hover:bg-secondary-100 rounded-lg transition-colors duration-200"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
          </div>

          {/* Events and Leaves List */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {events?.length === 0 && leaves?.length === 0 && employeeEvents?.length === 0 ? (
              <div className="text-center py-8">
                <Icon
                  name="Calendar"
                  size={48}
                  color="var(--color-secondary-300)"
                  className="mx-auto mb-4"
                />
                <p className="text-text-secondary">No events or leaves for this date</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Events Section */}
                {events?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-text-primary mb-3 flex items-center">
                      <Icon name="Calendar" size={16} className="mr-2" />
                      Events ({events?.length})
                    </h4>
                    <div className="space-y-3">
                      {events?.map((event, index) => (
                        <div key={index} className="border border-border rounded-lg p-4">
                          {editingEvent === index && isAdmin ? (
                            /* Edit Mode - Only for admins */
                            <div className="space-y-3">
                              <input
                                type="text"
                                value={editTitle}
                                onChange={(e) => setEditTitle(e?.target?.value)}
                                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                autoFocus
                              />
                              <div className="space-y-2">
                                {eventTypes?.map((eventType) => (
                                  <label
                                    key={eventType?.value}
                                    className="flex items-center space-x-3 cursor-pointer"
                                  >
                                    <input
                                      type="radio"
                                      name={`eventType-${index}`}
                                      value={eventType?.value}
                                      checked={editType === eventType?.value}
                                      onChange={(e) => setEditType(e?.target?.value)}
                                      className="w-4 h-4 text-primary border-border focus:ring-primary-500"
                                    />
                                    <div className="flex items-center space-x-2">
                                      <Icon
                                        name={eventType?.icon}
                                        size={16}
                                        className={eventType?.color}
                                      />
                                      <span className="text-sm text-text-primary">
                                        {eventType?.label}
                                      </span>
                                    </div>
                                  </label>
                                ))}
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleEditSave(index)}
                                  className="flex items-center space-x-2 px-3 py-1.5 text-success bg-success-50 hover:bg-success-100 rounded-lg transition-colors duration-200"
                                >
                                  <Icon name="Check" size={14} />
                                  <span className="text-sm">Save</span>
                                </button>
                                <button
                                  onClick={handleEditCancel}
                                  className="flex items-center space-x-2 px-3 py-1.5 text-text-secondary bg-secondary-100 hover:bg-secondary-200 rounded-lg transition-colors duration-200"
                                >
                                  <Icon name="X" size={14} />
                                  <span className="text-sm">Cancel</span>
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* Display Mode */
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <Icon
                                  name={getEventTypeData(event?.type)?.icon}
                                  size={20}
                                  className={getEventTypeData(event?.type)?.color}
                                />
                                <div>
                                  <p className="font-medium text-text-primary">{event?.title}</p>
                                  <p className="text-sm text-text-secondary">{event?.type}</p>
                                </div>
                              </div>
                              {isAdmin && (
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleEditStart(event, index)}
                                    className="p-2 text-primary hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors duration-200"
                                    title="Edit event"
                                  >
                                    <Icon name="Edit" size={16} />
                                  </button>
                                  <button
                                    onClick={() => onDelete?.(index)}
                                    className="p-2 text-error hover:text-error-700 hover:bg-error-50 rounded-lg transition-colors duration-200"
                                    title="Delete event"
                                  >
                                    <Icon name="Trash2" size={16} />
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {employeeEvents?.length > 0 && (
                  <div>
                    <div className="space-y-3">
                      {employeeEvents?.map((event, index) => (
                        <div key={index} className="border border-border rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <Icon
                                name={event.type == 'Birthday' ? 'Cake' : 'Award'}
                                size={20}
                                className={getEventTypeData(event?.type)?.color}
                              />
                              <div>
                                <p className="font-medium text-text-primary">{event?.name}</p>
                                <p className="text-sm text-text-secondary">{event?.type}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Leaves Section */}
                {leaves?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-text-primary mb-3 flex items-center">
                      <Icon name="Users" size={16} className="mr-2" />
                      Leaves ({leaves?.length})
                    </h4>
                    <div className="space-y-3">
                      {leaves?.map((leave, index) => (
                        <div
                          key={`leave-${index}`}
                          className="border border-border rounded-lg p-4 bg-purple-50"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3 flex-1">
                              <div className="p-2 bg-purple-100 rounded-lg">
                                <Icon
                                  name={getLeaveTypeIcon(leave?.type)}
                                  size={16}
                                  className={getLeaveTypeColor(leave?.type)}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="font-medium text-text-primary truncate">
                                    {getEmployeeDisplayName(leave)}
                                  </p>
                                  <span
                                    className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(leave?.status)}`}
                                  >
                                    {leave?.status}
                                  </span>
                                </div>

                                <p className="text-sm text-purple-600 font-medium mb-2">
                                  {mapLeaveType(leave?.type)}
                                </p>

                                {/* Date and Time Information */}
                                <div className="space-y-1 mb-2">
                                  <p className="text-xs text-text-secondary">
                                    <Icon name="Calendar" size={12} className="inline mr-1" />
                                    <span className="font-medium">Date:</span>{' '}
                                    {formatLeaveDate(leave)}
                                  </p>

                                  {/* Show detailed date/time for datetime-based leaves */}
                                  {/* {leave?.start_date_time && leave?.end_date_time && (
                                    <p className="text-xs text-text-secondary">
                                      <Icon name="Clock" size={12} className="inline mr-1" />
                                      <span className="font-medium">Time:</span>{' '}
                                      {formatLeaveDateTime(leave)}
                                    </p>
                                  )} */}

                                  {/* Show specific dates for Multiple Day Leave */}
                                  {leave?.type === LEAVE_TYPES.MULTIPLE_DAY &&
                                    leave?.dates?.length > 0 && (
                                      <p className="text-xs text-text-secondary">
                                        <Icon name="Calendar" size={12} className="inline mr-1" />
                                        <span className="font-medium">Selected Dates:</span>{' '}
                                        {formatMultipleDayDates(leave)}
                                      </p>
                                    )}

                                  {/* Duration */}
                                  <p className="text-xs text-text-secondary">
                                    <Icon name="Clock" size={12} className="inline mr-1" />
                                    <span className="font-medium">Duration:</span>{' '}
                                    {formatLeaveDuration(leave)}
                                  </p>

                                  {/* Applied Date */}
                                  {leave?.appliedAt && (
                                    <p className="text-xs text-text-secondary">
                                      <Icon name="Calendar" size={12} className="inline mr-1" />
                                      <span className="font-medium">Applied:</span>{' '}
                                      {new Date(leave?.appliedAt).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>

                                {/* Reason */}
                                {leave?.reason && (
                                  <p
                                    className="text-xs text-text-secondary truncate"
                                    title={leave?.reason}
                                  >
                                    <Icon name="MessageSquare" size={12} className="inline mr-1" />
                                    <span className="font-medium">Reason:</span> {leave?.reason}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {events?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <button
                onClick={() => onAdd?.(selectedDate)}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-primary bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors duration-200"
              >
                <Icon name="Plus" size={16} />
                <span>{isAdmin ? 'Add Another Event' : 'Add Leave'}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventListModal;
